import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

/**
 * Convert any API error shape into a safe, printable string.
 * FastAPI 422 returns `detail: [{type, loc, msg, input, url}, ...]` which would
 * crash React if passed to `toast.error()` directly.
 */
export function describeApiError(err, fallback = "Something went wrong") {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    return d
      .map((e) => {
        if (typeof e === "string") return e;
        const field = Array.isArray(e?.loc) ? e.loc.slice(-1)[0] : null;
        return field ? `${field}: ${e?.msg || "invalid"}` : (e?.msg || JSON.stringify(e));
      })
      .join(" · ");
  }
  if (d && typeof d === "object") return d.msg || JSON.stringify(d);
  return err?.message || fallback;
}

// Normalize FastAPI validation arrays into a string on every response so even
// legacy `toast.error(e?.response?.data?.detail)` call-sites stay React-safe.
// IMPORTANT: Only stringify ARRAYS (FastAPI 422 validation errors). Preserve
// structured dict details (iter59+ e.g. duplicate_email_active with
// archived_match) so callers can branch on detail.code / detail.archived_match.
api.interceptors.response.use(
  (resp) => resp,
  (error) => {
    try {
      const detail = error?.response?.data?.detail;
      if (Array.isArray(detail)) {
        error.response.data.detail = describeApiError(error);
      }
    } catch { /* noop */ }
    return Promise.reject(error);
  },
);

// Brand constants
export const BRAND = {
  name: "JDSN Lawn Care",
  phone: "904-832-8730",
  phoneTel: "+19048328730",
  phoneRaw: "9048328730",
  city: "Jacksonville",
  state: "Florida",
  email: "jdsnlawncare@gmail.com",
};

export const SERVICE_AREAS = [
  "Orange Park", "Arlington", "Mandarin", "Southside", "Westside", "Northside",
  "Riverside", "Oakleaf", "Jacksonville Beach", "Ponte Vedra", "St Johns",
  "Middleburg", "Fleming Island",
];

// iter85: Customer-facing payment methods reduced to Stripe only. Offline
// methods remain in the admin CRM catalog + historical booking chips via
// PaymentMethodChip / PAY_METHOD_LABEL — no shared config needed for those.
export const PAYMENT_METHODS = [
  { id: "stripe", label: "Credit / Debit Card", note: "Secure checkout via Stripe" },
];

export const BEFORE_AFTER = [
  {
    title: "Westside — Lawn mowing & cleanup",
    before: "/gallery/before-mowing-thumb.jpg",
    after: "/gallery/after-mowing-thumb.jpg",
    beforeFull: "/gallery/before-mowing.jpg",
    afterFull: "/gallery/after-mowing.jpg",
  },
  {
    title: "Northside — Yard cleanup & weed eating",
    before: "/gallery/before-cleanup-thumb.jpg",
    after: "/gallery/after-cleanup-thumb.jpg",
    beforeFull: "/gallery/before-cleanup.jpg",
    afterFull: "/gallery/after-cleanup.jpg",
  },
  {
    title: "Front yard restoration",
    before: "/gallery/before-cleanup-thumb.jpg",
    after: "/gallery/after-mowing-thumb.jpg",
    beforeFull: "/gallery/before-cleanup.jpg",
    afterFull: "/gallery/after-mowing.jpg",
  },
];

export const SHOWCASE_IMAGES = [
  "/gallery/after-mowing.jpg",
  "/gallery/after-cleanup.jpg",
  "/gallery/before-mowing.jpg",
  "/gallery/before-cleanup.jpg",
];

export const FAQS = [
  { q: "What areas of Jacksonville do you cover?", a: "We cover all of Jacksonville including Orange Park, Arlington, Mandarin, Southside, Westside, Northside, Riverside, Oakleaf, Jacksonville Beach, Ponte Vedra, St Johns, Middleburg and Fleming Island." },
  { q: "How fast can you start?", a: "Most jobs scheduled within 24-48 hours. Recurring lawn mowing customers get priority same-week slots." },
  { q: "Is the estimate free?", a: "Yes — instant online estimates are 100% free. We also offer on-site estimates for landscaping & drainage projects." },
  { q: "What payment methods do you accept?", a: "Credit / Debit cards via Stripe — Visa, Mastercard, Amex, Apple Pay and Google Pay. Save your card for one-tap rebooking on recurring visits." },
  { q: "Do you offer recurring service?", a: "Yes — weekly, bi-weekly and monthly plans available. Recurring customers save 10-15% per visit." },
];
